if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        FoxTheme.initProductForms?.(this);
        FoxTheme.initSubscriptionWidgets?.(this);
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        this.assignProductSectionToForms();
        if (this.currentVariant) {
          this.syncSubscriptionForVariant(this.currentVariant);
          this.updateMedia(this.currentVariant);
          this.initPricePerDay(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.destroySubscriptionWidgets();
      }

      destroySubscriptionWidgets() {
        this.querySelectorAll('.subscription-widget').forEach((container) => {
          container.subscriptionWidget?.destroy();
        });
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          FoxTheme.initProductForms?.(newNode || document);
          FoxTheme.initSubscriptionWidgets?.(newNode || document);
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedOptionNames } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        if (shouldSwapProduct) {
          FoxTheme.productFormSubmissionCoordinator.setPending(this.sectionId, true);
        } else {
          const localVariant = this.resolveLocalVariant(selectedOptionNames);
          this.prepareLocalVariant(localVariant);
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[data-product-form]');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        FoxTheme.getProductFormController?.(productForm)?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);
          this.destroySubscriptionWidgets();

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;

          const updatedProductInfo = document.querySelector(
            `product-info[data-section="${this.dataset.section}"]`
          );
          updatedProductInfo?.finalizeVariantSubmission(variant);
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.finalizeVariantSubmission(null);
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          const updateLoyaltyPoints = (variant) => {
            const loyaltyDisplay = this.querySelector('.loyalty-points-display');
            if (loyaltyDisplay && variant) {
              const points = Math.floor(variant.price / 100)*10;
              loyaltyDisplay.innerHTML = `Ce produit vous rapportera <b>${points}</b> points de fidélité.`;
            }
          };

          const updateMetafields = (variant) => {
            const metafieldsDisplay = this.querySelector('.product-metafields');
            if (metafieldsDisplay) {
              const sourceMetafields = html.querySelector('.product-metafields');
              if (sourceMetafields) {
                metafieldsDisplay.classList.toggle('hidden', sourceMetafields.classList.contains('hidden'));
                metafieldsDisplay.innerHTML = sourceMetafields.innerHTML;
                
                // Calculer et afficher le prix par jour
                const pricePerDayElement = metafieldsDisplay.querySelector('.price-per-day');
                if (pricePerDayElement && variant) {
                  const joursCure = parseInt(sourceMetafields.dataset.joursCure) || 0;
                  if (joursCure > 0) {
                    const pricePerDay = variant.price / (100 * joursCure);
                    const formattedPrice = new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(pricePerDay).replace(/\s/g, '');
                    const perDayText = window.theme?.strings?.perDay || 'par jour';
                    pricePerDayElement.textContent = formattedPrice + ' ' + perDayText;
                  }
                }
              }
            }
          };
          
          updateLoyaltyPoints(variant);
          updateMetafields(variant);

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const updateButtonText = (buttonElement) => {
            const btnText = buttonElement.querySelector('.btn__text');
            if (btnText && variant) {
              if (variant.available) {
                const price = new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(variant.price/100).replace(/\s/g, '');
                btnText.innerHTML = FoxTheme.getProductSubmitContent(buttonElement, price);
              } else {
                btnText.innerHTML = FoxTheme.variantStrings.soldOut;
              }
            }
          };

          const mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          if (mainButton) {
            updateButtonText(mainButton);
          }

          const stickyButton = document.getElementById(`ProductSubmitButton-sticky-${this.sectionId}`);
          if (stickyButton) {
            updateButtonText(stickyButton);
          }

          this.toggleAddButton(
            !variant?.available,
            FoxTheme.variantStrings.soldOut,
            false
          );

          this.currentVariant = variant;

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.finalizeVariantSubmission(variant);
        };
      }

      getVariantData(productInfoNode = this) {
        if (!productInfoNode) return [];

        const productInfo = productInfoNode.matches?.('product-info')
          ? productInfoNode
          : productInfoNode.querySelector?.('product-info') || productInfoNode;
        const variantDataElement =
          productInfo.querySelector?.(':scope > [data-product-variants]') ||
          productInfo.querySelector?.('[data-product-variants]');
        if (!variantDataElement) return [];

        try {
          const variants = JSON.parse(variantDataElement.textContent);
          return Array.isArray(variants) ? variants : [];
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      resolveLocalVariant(selectedOptionNames = this.variantSelectors?.selectedOptionNames || []) {
        if (!selectedOptionNames.length) return null;

        return (
          this.getVariantData().find((variant) => {
            return variant.options?.every((option, index) => option === selectedOptionNames[index]);
          }) || null
        );
      }

      prepareLocalVariant(variant) {
        if (!variant) {
          this.updateVariantInputs(null);
          FoxTheme.productFormSubmissionCoordinator.setPending(this.sectionId, true);
          return;
        }

        this.updateVariantInputs(variant.id);
        this.currentVariant = variant;
        this.toggleAddButton(!variant.available, FoxTheme.variantStrings.soldOut, false);

        const subscriptionReady = this.syncSubscriptionForVariant(variant);
        if (subscriptionReady) this.setSubscriptionBlocked(false);
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant,
            local: true,
          },
        });

        FoxTheme.productFormSubmissionCoordinator.setPending(
          this.sectionId,
          variant.available && !subscriptionReady
        );
      }

      assignProductSectionToForms() {
        this.getProductForms().forEach((form) => {
          form.dataset.productSection = this.sectionId;
        });
      }

      getProductForms() {
        return [
          document.getElementById(`product-form-${this.dataset.section}`),
          document.getElementById(`product-form-sticky-${this.dataset.section}`),
        ].filter(Boolean);
      }

      syncSubscriptionForVariant(variant) {
        const container = this.querySelector('.subscription-widget');
        if (!container) return !variant?.requires_selling_plan;

        if (!container.subscriptionWidget && window.SubscriptionWidget) {
          new window.SubscriptionWidget(container);
        }

        if (!container.subscriptionWidget) return !variant?.requires_selling_plan;
        return container.subscriptionWidget.handleVariantChange(variant) !== false;
      }

      finalizeVariantSubmission(variant) {
        this.assignProductSectionToForms();
        const subscriptionReady = Boolean(variant?.available) && this.syncSubscriptionForVariant(variant);
        const canSubmit = Boolean(variant?.available && subscriptionReady);

        if (variant?.available && !subscriptionReady) {
          this.setSubscriptionBlocked(true);
        } else if (subscriptionReady) {
          this.setSubscriptionBlocked(false);
        }

        FoxTheme.productFormSubmissionCoordinator.resolve(this.sectionId, {
          canSubmit,
          errorMessage: false,
        });
      }

      handleSubscriptionReadinessChange(isReady) {
        if (!this.currentVariant?.available) return;

        FoxTheme.productFormSubmissionCoordinator.setPending(this.sectionId, !isReady);
        this.setSubscriptionBlocked(!isReady);
      }

      setSubscriptionBlocked(isBlocked) {
        this.getProductForms().forEach((form) => {
          const button = form.querySelector('[name="add"]');
          if (!button) return;

          if (isBlocked) {
            button.dataset.subscriptionBlocked = 'true';
            button.disabled = true;
          } else if (button.dataset.subscriptionBlocked === 'true') {
            delete button.dataset.subscriptionBlocked;
            button.disabled = false;
          }
        });
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;

        const productInfo = productInfoNode.matches?.('product-info')
          ? productInfoNode
          : productInfoNode.querySelector?.('product-info') || productInfoNode;
        const selectedVariant = productInfo.querySelector?.('variant-selects [data-selected-variant]')?.textContent;

        if (selectedVariant) {
          try {
            return JSON.parse(selectedVariant);
          } catch (error) {
            console.error(error);
          }
        }

        // Shopify does not render <variant-selects> for products that only have
        // the default variant. Resolve it from the form/embedded variant data so
        // these products can still be added to the cart (including preorders).
        const variants = this.getVariantData(productInfo);
        const variantId = Number(
          productInfo.querySelector?.('form[data-product-form] [name="id"]')?.value
        );

        return variants.find((variant) => variant.id === variantId) || (variants.length === 1 ? variants[0] : null);
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              FoxTheme.productFormSubmissionCoordinator.resolve(this.sectionId, {
                canSubmit: false,
                errorMessage: FoxTheme.variantStrings.unavailable,
              });
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        const currentVariantSelects = this.variantSelectors;
        if (!variantSelects || !currentVariantSelects) return;

        this.preProcessHtmlCallbacks?.forEach((callback) => callback(variantSelects));
        currentVariantSelects.replaceWith(variantSelects);
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-sticky-${this.dataset.section}`
          )
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            if (!input) return;
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const updateButton = (button) => {
          if (button) {
            if (disable) {
              button.setAttribute('disabled', 'disabled');
            } else {
              button.removeAttribute('disabled');
            }
          }
        };

        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (productForm) {
          updateButton(productForm.querySelector('[name="add"]'));
        }

        const stickyForm = document.getElementById(`product-form-sticky-${this.dataset.section}`);
        if (stickyForm) {
          updateButton(stickyForm.querySelector('[name="add"]'));
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.querySelector('[name="id"]')?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      initPricePerDay(variant) {
        const metafieldsDisplay = this.querySelector('.product-metafields');
        if (metafieldsDisplay && variant) {
          const pricePerDayElement = metafieldsDisplay.querySelector('.price-per-day');
          const joursCure = parseInt(metafieldsDisplay.dataset.joursCure) || 0;
          
          if (pricePerDayElement && joursCure > 0) {
            const pricePerDay = variant.price / (100 * joursCure);
            const formattedPrice = new Intl.NumberFormat('fr-FR', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(pricePerDay).replace(/\s/g, '');
            const perDayText = window.theme?.strings?.perDay || 'par jour';
            pricePerDayElement.textContent = formattedPrice + ' ' + perDayText;
          }
        }
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');

        if (!this.buttonEl) {
          console.warn('[HélicA] Aucun bouton trouvé dans <product-promotion-alert>');
          return;
        }

        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();
        const productBlock = this.closest('.product__block');
        if (productBlock) {
          productBlock.classList.add('hidden');
        }
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
